import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCanvas } from "../../../../context/CanvasContext";
import ConfirmationModal from "../../../../../shared/components/modals/ConfirmationModal";
import EditModal from "../../../../../shared/components/modals/EditModal";
import InstructionsModal from "../../../../../shared/components/InstructionsModal";
import GenericDetailsModal from "../../../../../shared/components/modals/GenericDetailsModal";
import GenericCard from "../../../../../shared/components/GenericCard";
import { FaMoneyCheckAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import {
  fetchMortgages,
  createMortgage,
  updateMortgage,
  deleteMortgage,
  uploadMortgages,
  selectMortgages,
  selectMortgageLoading,
  selectMortgagePagination,
  mortgageOverview,
  selectMortgageOverview,
} from "../../../../../../app/store/slices/mortgageSlice";
import { selectAccounts, fetchAccounts } from "../../../../../../app/store/slices/accountSlice";
import Pagination from "../../../../../shared/components/Pagination";
import { MORTGAGE_STATUS_OPTIONS } from "../../../../../../utils/constants/dashboardConstants";
import FileUploadButton from "../../../../../shared/components/FileUploadButton";
import FormInput from "../../../../../shared/ui/FormInput";
import FormSelect from "../../../../../shared/ui/FormSelect";
import FormTextarea from "../../../../../shared/ui/FormTextarea";
import Spinner from '../../../../../shared/components/Spinner';
import useClickOutside from "../../../../../shared/hooks/useClickOutside";
import SummaryCard from "../../../../../shared/components/SummaryCard";
import { FaCheck } from 'react-icons/fa';
import { FiPercent } from "react-icons/fi";
import AddOptionsButton from "../../../../../shared/components/AddOptionsButton";
import {formatAmount} from '../../../../../../utils/formatAmount';
import { 
  calculateNextDueDate, 
  getSchedulePreviewText, 
  dueDateTypes, 
  months, 
  monthDays,
  shouldShowDay,
  shouldShowMonth,
  getCurrentMonth 
} from '../../../../../../utils/dueDateCalculator';

const MortgageContent = () => {
  const dispatch = useDispatch();
  const { selectedCanvas } = useCanvas();
  const mortgages = useSelector(selectMortgages);
  const loading = useSelector(selectMortgageLoading);
  const pagination = useSelector(selectMortgagePagination);
  const accounts = useSelector(selectAccounts);
  const overview = useSelector(selectMortgageOverview);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAddOptions, setShowAddOptions] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [selectedMortgage, setSelectedMortgage] = useState(null);
  const [editMortgageData, setEditMortgageData] = useState({
    propertyName: "",
    initialAmount: "",
    mortgageStartDate: "",
    annualInterestRate: "",
    monthlyPayment: "",
    monthlyPaymentDueDate: "",
    mortgageDueDate: '',
    amountRepaid: "",
    propertyValue: "",
    status: "active",
    paymentAccountId: "",
    notes: "",
    paymentDueDateType: "",
    paymentDueDateDay: "",
    paymentDueDateMonth: ""
  });
  const [addMortgageData, setAddMortgageData] = useState({
    propertyName: "",
    initialAmount: "",
    mortgageStartDate: "",
    annualInterestRate: "",
    monthlyPayment: "",
    monthlyPaymentDueDate: "",
    mortgageDueDate:'',
    amountRepaid: "",
    propertyValue: "",
    status: "active",
    paymentAccountId: "",
    notes: "",
    paymentDueDateType: "",
    paymentDueDateDay: "",
    paymentDueDateMonth: ""
  });

  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const fileInputRef = useRef();
  const addOptionsRef = useRef(null);
  const baseCurrency = selectedCanvas?.currency || "";
  
  useClickOutside(addOptionsRef, () => setShowAddOptions(false), showAddOptions);

  const formatCurrency = (amount) => {
    return `${baseCurrency} ${formatAmount(amount)}`;
  };

  useEffect(() => {
    if (addMortgageData.paymentDueDateType && addMortgageData.paymentDueDateMonth) {
      const calculatedDate = calculateNextDueDate(addMortgageData.paymentDueDateType, addMortgageData.paymentDueDateMonth, addMortgageData.paymentDueDateDay);
      if (calculatedDate && calculatedDate !== addMortgageData.monthlyPaymentDueDate) {
        setAddMortgageData(prev => ({ ...prev, monthlyPaymentDueDate: calculatedDate }));
      }
    }
  }, [addMortgageData.paymentDueDateType, addMortgageData.paymentDueDateMonth, addMortgageData.paymentDueDateDay]);

  useEffect(() => {
    if (editMortgageData.paymentDueDateType && editMortgageData.paymentDueDateMonth) {
      const calculatedDate = calculateNextDueDate(editMortgageData.paymentDueDateType, editMortgageData.paymentDueDateMonth, editMortgageData.paymentDueDateDay);
      if (calculatedDate && calculatedDate !== editMortgageData.monthlyPaymentDueDate) {
        setEditMortgageData(prev => ({ ...prev, monthlyPaymentDueDate: calculatedDate }));
      }
    }
  }, [editMortgageData.paymentDueDateType, editMortgageData.paymentDueDateMonth, editMortgageData.paymentDueDateDay]);

  useEffect(() => {
    if (addMortgageData.paymentDueDateType && !addMortgageData.paymentDueDateMonth) {
      setAddMortgageData(prev => ({ ...prev, paymentDueDateMonth: getCurrentMonth() }));
    }
  }, [addMortgageData.paymentDueDateType]);

  useEffect(() => {
    if (editMortgageData.paymentDueDateType && !editMortgageData.paymentDueDateMonth) {
      setEditMortgageData(prev => ({ ...prev, paymentDueDateMonth: getCurrentMonth() }));
    }
  }, [editMortgageData.paymentDueDateType]);

  // File upload template download
  const handleDownloadTemplate = () => {
    try {
      const sampleAccountName = accounts.length > 0 ? accounts[0].name : 'account_name_here';
      

      const templateData = [
        { 
          propertyName: 'Main Residence', 
          initialAmount: '350000', 
          mortgageStartDate: '2020-01-15',
          mortgageDueDate:'2027-02-15',
          annualInterestRate: '3.5',
          monthlyPayment: '1850',
          monthlyPaymentDueDate: '1',
          amountRepaid: '75000', 
          propertyValue: '420000',
          currency: baseCurrency,
          status: 'active',
          paymentAccountId: sampleAccountName,
          notes: 'Primary residence mortgage'
        },
        { 
          propertyName: 'Investment Property', 
          initialAmount: '250000', 
          mortgageStartDate: '2021-06-01',
          mortgageDueDate:'2026-06-02',
          annualInterestRate: '4.2',
          monthlyPayment: '1350',
          monthlyPaymentDueDate: '15',
          amountRepaid: '35000', 
          propertyValue: '280000',
          currency: baseCurrency,
          status: 'active',
          paymentAccountId: sampleAccountName,
          notes: 'Rental property investment'
        },
        { 
          propertyName: 'Vacation Home', 
          initialAmount: '180000', 
          mortgageStartDate: '2019-03-01',
          mortgageDueDate: '2029-03-01',
          annualInterestRate: '4.8',
          monthlyPayment: '1120',
          monthlyPaymentDueDate: '5',
          amountRepaid: '65000', 
          propertyValue: '210000',
          currency: baseCurrency,
          status: 'active',
          paymentAccountId: sampleAccountName,
          notes: 'Mountain cabin mortgage'
        }
      ];

      const csvContent = [
        'propertyName,initialAmount,mortgageStartDate,mortgageDueDate,annualInterestRate,monthlyPayment,monthlyPaymentDueDate,amountRepaid,propertyValue,currency,status,paymentAccountId,notes',
        ...templateData.map(row => 
          `${row.propertyName},${row.initialAmount},${row.mortgageStartDate},${row.mortgageDueDate},${row.annualInterestRate},${row.monthlyPayment},${row.monthlyPaymentDueDate},${row.amountRepaid},${row.propertyValue},${row.currency},${row.status},${row.paymentAccountId},"${row.notes}"`
        )
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mortgages_template_${baseCurrency}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setShowInstructions(false);
    } catch (error) {
      toast.error('Failed to download template');
      console.error('Template download error:', error);
    }
  };

  useEffect(() => {
    if (selectedCanvas?.id) {
      dispatch(fetchMortgages({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(fetchAccounts({ canvasId: selectedCanvas.id, limit:1000 }));
      dispatch(mortgageOverview({ canvasId: selectedCanvas.id }));
    }
  }, [dispatch, selectedCanvas?.id, currentPage, pageSize]);

  useEffect(() => {
    if (showEditForm && selectedMortgage) {
      setEditMortgageData({
        propertyName: selectedMortgage.propertyName || "",
        initialAmount: selectedMortgage.initialAmount ?? "",
        mortgageStartDate: selectedMortgage.mortgageStartDate ? selectedMortgage.mortgageStartDate.slice(0, 10) : "",
        annualInterestRate: selectedMortgage.annualInterestRate ?? "",
        monthlyPayment: selectedMortgage.monthlyPayment ?? "",
        monthlyPaymentDueDate: selectedMortgage.monthlyPaymentDueDate ? selectedMortgage.monthlyPaymentDueDate.slice(0, 10) : "",
        mortgageDueDate: selectedMortgage?.mortgageDueDate ?? '',
        amountRepaid: selectedMortgage.amountRepaid ?? "",
        propertyValue: selectedMortgage.propertyValue ?? "",
        status: selectedMortgage.status || "active",
        paymentAccountId: selectedMortgage.paymentAccountId || "",
        notes: selectedMortgage.notes || "",
        paymentDueDateType: selectedMortgage.paymentDueDateType || "",
        paymentDueDateDay: selectedMortgage.paymentDueDateDay || "",
        paymentDueDateMonth: selectedMortgage.paymentDueDateMonth || ""
      });
    }
  }, [showEditForm, selectedMortgage]);

  // Handle Delete
  const handleDeleteMortgage = async () => {
    if (!selectedMortgage || !selectedCanvas?.id) {
      toast.error("Invalid Canvas or Mortgage");
      return;
    }
    setIsDeleting(true);
    try {
      await dispatch(
        deleteMortgage({
          canvasId: selectedCanvas.id,
          mortgageId: selectedMortgage._id,
        })
      ).unwrap();
      setShowDeleteConfirm(false);
      setSelectedMortgage(null);
      dispatch(fetchMortgages({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(mortgageOverview({ canvasId: selectedCanvas.id }));
      toast.success("Mortgage Deleted Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle Edit
  const handleEditMortgage = async (e) => {
    e.preventDefault();
    if (!selectedMortgage || !selectedCanvas?.id) {
      toast.error("Invalid Canvas or Mortgage");
      return;
    }
    setIsEditing(true);
    try {
      await dispatch(
        updateMortgage({
          canvasId: selectedCanvas.id,
          mortgageId: selectedMortgage._id,
          mortgageData: {
            ...editMortgageData,
            initialAmount: Number(Number(editMortgageData.initialAmount).toFixed(2)),
            annualInterestRate: Number(Number(editMortgageData.annualInterestRate).toFixed(2)),
            monthlyPayment: Number(Number(editMortgageData.monthlyPayment).toFixed(2)),
            amountRepaid: Number(Number(editMortgageData.amountRepaid).toFixed(2)),
            propertyValue: Number(Number(editMortgageData.propertyValue).toFixed(2)),
            paymentDueDateType: editMortgageData.paymentDueDateType,
            paymentDueDateDay: editMortgageData.paymentDueDateDay,
            paymentDueDateMonth: editMortgageData.paymentDueDateMonth
          },
        })
      ).unwrap();
      setShowEditForm(false);
      setSelectedMortgage(null);
      dispatch(fetchMortgages({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(mortgageOverview({ canvasId: selectedCanvas.id }));
      toast.success("Mortgage Updated Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsEditing(false);
    }
  };

  // Handle Add
  const handleAddMortgage = async (e) => {
    e.preventDefault();
    if (!selectedCanvas?.id) {
      toast.error("Invalid Canvas");
      return;
    }
    setIsAdding(true);
    try {
      await dispatch(
        createMortgage({
          canvasId: selectedCanvas.id,
          mortgageData: {
            ...addMortgageData,
            initialAmount: Number(Number(addMortgageData.initialAmount).toFixed(2)),
            annualInterestRate: Number(Number(addMortgageData.annualInterestRate).toFixed(2)),
            monthlyPayment: Number(Number(addMortgageData.monthlyPayment).toFixed(2)),
            amountRepaid: Number(Number(addMortgageData.amountRepaid || 0).toFixed(2)),
            propertyValue: Number(Number(addMortgageData.propertyValue).toFixed(2)),
            paymentDueDateType: addMortgageData.paymentDueDateType,
            paymentDueDateDay: addMortgageData.paymentDueDateDay,
            paymentDueDateMonth: addMortgageData.paymentDueDateMonth
          },
        })
      ).unwrap();
      setShowAddForm(false);
      setAddMortgageData({
        propertyName: "",
        initialAmount: "",
        mortgageStartDate: "",
        annualInterestRate: "",
        monthlyPayment: "",
        monthlyPaymentDueDate: "",
        mortgageDueDate: "",
        amountRepaid: "",
        propertyValue: "",
        status: "active",
        paymentAccountId: "",
        notes: "",
        paymentDueDateType: "",
        paymentDueDateDay: "",
        paymentDueDateMonth: ""
      });
      dispatch(fetchMortgages({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(mortgageOverview({ canvasId: selectedCanvas.id }));
      toast.success("Mortgage Created Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    } finally {
      setIsAdding(false);
    }
  };

  const handleUpdateMortgage = async (mortgageId, updateData) => {
    setIsUpdating(true);
    try {
      await dispatch(
        updateMortgage({
          canvasId: selectedCanvas.id,
          mortgageId,
          mortgageData: updateData,
        })
      ).unwrap();
      
      setSelectedMortgage(prev => ({ ...prev, ...updateData }));
      
      dispatch(fetchMortgages({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(mortgageOverview({ canvasId: selectedCanvas.id }));
      toast.success("Mortgage updated successfully");
    } catch (err) {
      toast.error(err?.message || 'Failed to update mortgage');
      throw err;
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle Add Payment
  const handleAddPayment = async (mortgageId, amount) => {
    try {
      const mortgage = mortgages.find(m => m._id === mortgageId);
      if (!mortgage) {
        toast.error("Mortgage not found");
        return;
      }

      const newAmountRepaid = (mortgage.amountRepaid || 0) + amount;
      const newBalance = mortgage.initialAmount - newAmountRepaid;
      
      const updateData = { 
        amountRepaid: newAmountRepaid,
        balance: newBalance 
      };
      
      if (newBalance <= 0 && mortgage.status !== 'completed') {
        updateData.status = 'completed';
      }
      
      await handleUpdateMortgage(mortgageId, updateData);
      
      const statusMessage = newBalance <= 0 && mortgage.status !== 'completed' 
        ? ` - Congratulations! Your mortgage is now fully paid! 🎉`
        : '';
      
      dispatch(mortgageOverview({ canvasId: selectedCanvas.id }));
      toast.success(`Payment of ${formatCurrency(amount)} recorded successfully${statusMessage}`);
    } catch (err) {
      toast.error('Failed to record payment');
      throw err;
    }
  };

  // Handle file upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedCanvas?.id) {
      toast.error("Invalid Canvas ID or file");
      return;
    }
    try {
      await dispatch(uploadMortgages({ canvasId: selectedCanvas.id, file })).unwrap();
      dispatch(fetchMortgages({ canvasId: selectedCanvas.id, page: currentPage, limit: pageSize }));
      dispatch(mortgageOverview({ canvasId: selectedCanvas.id }));
      toast.success("Mortgages Imported Successfully");
    } catch (err) {
      toast.error(err?.message || err);
    }
  };

  const transformMortgageForCard = (mortgage) => ({
    ...mortgage,
    title: mortgage.propertyName, 
    currentAmount: mortgage.amountRepaid || 0,
    balance: mortgage.initialAmount - (mortgage.amountRepaid || 0),
    estimatedFinalPaymentDate: mortgage.estimatedFinalPaymentDate,
  });

  // Monthly Payment Schedule Form Component
  const PaymentScheduleForm = ({ data, setData, prefix = "" }) => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Payment Due Type
          </label>
          <select
            value={data.paymentDueDateType}
            onChange={(e) => setData({ ...data, paymentDueDateType: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent"
          >
            <option value="">Select Type</option>
            {dueDateTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {shouldShowMonth(data.paymentDueDateType) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Starting Month
            </label>
            <select
              value={data.paymentDueDateMonth || ''}
              onChange={(e) => setData({ ...data, paymentDueDateMonth: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent"
            >
              <option value="">Select Month</option>
              {months.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {shouldShowDay(data.paymentDueDateType) && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Day
            </label>
            <select
              value={data.paymentDueDateDay || ''}
              onChange={(e) => setData({ ...data, paymentDueDateDay: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent"
            >
              <option value="">Select Day</option>
              {monthDays.map(day => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {(data.paymentDueDateType && data.paymentDueDateMonth) && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-sm text-green-800">
            <span className="font-medium">Payment Schedule Preview:</span>
            {getSchedulePreviewText(data.paymentDueDateType, data.paymentDueDateMonth, data.paymentDueDateDay)}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-xl md:text-2xl font-bold text-center mb-4 md:mb-6">Mortgages</h1>
      <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm">
      
      <div className="flex flex-wrap items-center justify-center md:justify-between gap-4 md:gap-8 mb-6">
        <div className="flex flex-wrap gap-4 md:gap-8 w-full md:w-auto">
          {/* Total Mortgage */}
           <SummaryCard
            title="Total Mortgages"
            value={formatAmount(overview?.totalMortgageAmount) !== undefined ? formatCurrency(overview?.totalMortgageAmount) : "Auto calculated"}
            icon={<FaMoneyCheckAlt className="w-5 h-5" />}
            valueColor="text-red-700"
            iconBg="bg-red-100"
            iconColor="text-red-600"
            formatValue={val => (formatCurrency((overview?.totalMortgageAmount)))}
          />
          <SummaryCard
            title="Total Monthly Payments"
            value={overview?.totalMonthlyPayments !== undefined ? overview.totalMonthlyPayments : "Auto calculated"}
            icon={<FaCheck className="w-5 h-5" />}
            valueColor="text-btn-primary"
            iconBg="bg-blue-100"
            iconColor="text-btn-primary"
            formatValue={val => typeof val === "number" ? `${formatAmount(val)}` : val}
          />
          <SummaryCard
            title="Mortgage Payment Rate"
            value={overview?.mortgagePayoffRate !== undefined ? overview.mortgagePayoffRate : "Auto calculated"}
            icon={<FiPercent className="w-5 h-5" />}
            valueColor="text-btn-primary"
            iconBg="bg-blue-100"
            iconColor="text-btn-primary"
            formatValue={val => typeof val === "number" ? `${val}%` : val}
          />
        </div>
        
       <AddOptionsButton
          showOptions={showAddOptions}
          setShowOptions={setShowAddOptions}
          addOptionsRef={addOptionsRef}
          onAddManual={() => setShowAddForm(true)}
          fileInputRef={fileInputRef}
          handleFileChange={handleFileChange}
          onShowInstructions={() => setShowInstructions(true)}
          buttonLabel="Record Mortgage"
          buttonTitle="Add Mortgage"
          manualLabel="Add Manually"
          FileUploadButtonComponent={FileUploadButton}
        />
      </div>

      {/* Mortgages Cards Grid */}
      
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Spinner text={"Loading Mortgages.."} size={'large'}/>
          </div>
        ) : mortgages?.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {mortgages.map(mortgage => (
                <GenericCard
                  key={mortgage._id}
                  item={transformMortgageForCard(mortgage)}
                  baseCurrency={baseCurrency}
                  cardType="mortgage"
                  onEdit={() => {
                    setSelectedMortgage(mortgage);
                    setShowEditForm(true);
                  }}
                  onDelete={() => {
                    setSelectedMortgage(mortgage);
                    setShowDeleteConfirm(true);
                  }}
                  onViewDetails={() => {
                    setSelectedMortgage(transformMortgageForCard(mortgage));
                    setShowDetailsModal(true);
                  }}
                />
              ))}
            </div>
            
            {/* Pagination */}
            <Pagination
              pagination={pagination}
              pageSize={pageSize}
              setPageSize={setPageSize}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              PAGE_SIZE_OPTIONS={[9, 18, 27]}
              label="Mortgages per page"
              totalLabel={`Total: ${pagination?.total || 0} mortgages`}
            />
          </>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <p className="text-gray-600 text-lg mb-2">No Mortgages Found</p>
            <p className="text-gray-500">Start tracking your property mortgages by adding your first mortgage record.</p>
          </div>
        )}
      </div>
      
      {/* Modals */}
      <ConfirmationModal
        show={showDeleteConfirm}
        title="Delete Mortgage"
        message={`Are you sure you want to delete mortgage for "${selectedMortgage?.propertyName}"? This action cannot be undone.`}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setSelectedMortgage(null);
        }}
        onConfirm={handleDeleteMortgage}
        loading={isDeleting}
      />

      <EditModal
        show={showEditForm}
        title="Edit Mortgage"
        onCancel={() => {
          setShowEditForm(false);
          setSelectedMortgage(null);
        }}
        onSubmit={handleEditMortgage}
        loading={isEditing}
        submitLabel="Update"
      >
        <div className="space-y-4 max-w-md w-full max-h-[80vh] overflow-y-auto p-2 sm:p-0">
          <FormInput
            label="Property Name"
            type="text"
            value={editMortgageData.propertyName}
            onChange={e => setEditMortgageData({ ...editMortgageData, propertyName: e.target.value })}
            required
          />
          <FormInput
            label="Initial Amount"
            type="number"
            value={editMortgageData.initialAmount}
            onChange={e => setEditMortgageData({ ...editMortgageData, initialAmount: e.target.value })}
            min="0"
            step="0.01"
            required
          />
          <FormInput
            label="Property Value"
            type="number"
            value={editMortgageData.propertyValue}
            onChange={e => setEditMortgageData({ ...editMortgageData, propertyValue: e.target.value })}
            min="0"
            step="0.01"
            required
          />
          <FormInput
            label="Annual Interest Rate (%)"
            type="number"
            value={editMortgageData.annualInterestRate}
            onChange={e => setEditMortgageData({ ...editMortgageData, annualInterestRate: e.target.value })}
            min="0"
            max="100"
            step="0.01"
            required
          />
          <FormInput
            label="Mortgage Start Date"
            type="date"
            value={editMortgageData.mortgageStartDate}
            onChange={e => setEditMortgageData({ ...editMortgageData, mortgageStartDate: e.target.value })}
            required
          />
          <FormInput
            label="Mortgage Due Date"
            type="date"
            value={editMortgageData.mortgageDueDate}
            onChange={e => setEditMortgageData({ ...editMortgageData, mortgageDueDate: e.target.value })}
            required
          />
          <FormInput
            label="Amount Repaid"
            type="number"
            value={editMortgageData.amountRepaid}
            onChange={e => setEditMortgageData({ ...editMortgageData, amountRepaid: e.target.value })}
            min="0"
            step="0.01"
          />
          <FormInput
            label="Monthly Payment"
            type="number"
            value={editMortgageData.monthlyPayment}
            onChange={e => setEditMortgageData({ ...editMortgageData, monthlyPayment: e.target.value })}
            min="0"
            step="0.01"
            required
          />
          
          {/* Payment Schedule Form for Edit */}
          <PaymentScheduleForm 
            data={editMortgageData} 
            setData={setEditMortgageData}
          />
  
          
          <FormSelect
            label="Status"
            value={editMortgageData.status}
            onChange={e => setEditMortgageData({ ...editMortgageData, status: e.target.value })}
            required
          >
            <option value="">Select Status</option>
            {MORTGAGE_STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </FormSelect>
          <FormSelect
            label="Payment Account"
            value={editMortgageData.paymentAccountId}
            onChange={e => setEditMortgageData({ ...editMortgageData, paymentAccountId: e.target.value })}
          >
            <option value="">Select Account</option>
            {accounts.map(acc => (
              <option key={acc._id} value={acc._id}>{acc.name}</option>
            ))}
          </FormSelect>
          <FormTextarea
            label="Notes"
            value={editMortgageData.notes}
            onChange={e => setEditMortgageData({ ...editMortgageData, notes: e.target.value })}
            rows={2}
          />
        </div>
      </EditModal>

      <EditModal
        show={showAddForm}
        title="Record Mortgage"
        onCancel={() => setShowAddForm(false)}
        onSubmit={handleAddMortgage}
        loading={isAdding}
        submitLabel="Record"
      >
        <div className="space-y-4 max-w-md w-full max-h-[80vh] overflow-y-auto p-2 sm:p-0">
          <FormInput
            label="Property Name"
            type="text"
            value={addMortgageData.propertyName}
            onChange={e => setAddMortgageData({ ...addMortgageData, propertyName: e.target.value })}
            required
          />
          <FormInput
            label="Initial Amount"
            type="number"
            value={addMortgageData.initialAmount}
            onChange={e => setAddMortgageData({ ...addMortgageData, initialAmount: e.target.value })}
            min="0"
            step="0.01"
            required
          />
          <FormInput
            label="Property Value"
            type="number"
            value={addMortgageData.propertyValue}
            onChange={e => setAddMortgageData({ ...addMortgageData, propertyValue: e.target.value })}
            min="0"
            step="0.01"
            required
          />
          <FormInput
            label="Annual Interest Rate (%)"
            type="number"
            value={addMortgageData.annualInterestRate}
            onChange={e => setAddMortgageData({ ...addMortgageData, annualInterestRate: e.target.value })}
            min="0"
            max="100"
            step="0.01"
            required
          />
          <FormInput
            label="Mortgage Start Date"
            type="date"
            value={addMortgageData.mortgageStartDate}
            onChange={e => setAddMortgageData({ ...addMortgageData, mortgageStartDate: e.target.value })}
            required
          />
          {/* <FormInput
            label="Mortgage Due Date"
            type="date"
            value={addMortgageData.mortgageDueDate}
            onChange={e => setAddMortgageData({ ...addMortgageData, mortgageDueDate: e.target.value })}
            required
          /> */}
          <FormInput
            label="Amount Repaid"
            type="number"
            value={addMortgageData.amountRepaid}
            onChange={e => setAddMortgageData({ ...addMortgageData, amountRepaid: e.target.value })}
            min="0"
            step="0.01"
            placeholder="0"
          />
          <FormInput
            label="Monthly Payment"
            type="number"
            value={addMortgageData.monthlyPayment}
            onChange={e => setAddMortgageData({ ...addMortgageData, monthlyPayment: e.target.value })}
            min="0"
            step="0.01"
            required
          />
          
          <PaymentScheduleForm 
            data={addMortgageData} 
            setData={setAddMortgageData}
          />  
          <FormSelect
            label="Status"
            value={addMortgageData.status}
            onChange={e => setAddMortgageData({ ...addMortgageData, status: e.target.value })}
            required
          >
            <option value="">Select Status</option>
            {MORTGAGE_STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </FormSelect>
          <FormSelect
            label="Payment Account"
            value={addMortgageData.paymentAccountId}
            onChange={e => setAddMortgageData({ ...addMortgageData, paymentAccountId: e.target.value })}
          >
            <option value="">Select Account</option>
            {accounts.map(acc => (
              <option key={acc._id} value={acc._id}>{acc.name}</option>
            ))}
          </FormSelect>
          <FormTextarea
            label="Notes"
            value={addMortgageData.notes}
            onChange={e => setAddMortgageData({ ...addMortgageData, notes: e.target.value })}
            rows={2}
            placeholder="Add notes about your mortgage (optional)"
          />
        </div>
      </EditModal>

      <GenericDetailsModal
        show={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedMortgage(null);
        }}
        item={selectedMortgage}
        onUpdateItem={handleUpdateMortgage}
        onAddTransaction={handleAddPayment}
        baseCurrency={baseCurrency}
        accounts={accounts}
        isUpdating={isUpdating}
        modalType="mortgage"
      />

      <InstructionsModal
        show={showInstructions}
        onClose={() => setShowInstructions(false)}
        type="mortgages"
        onDownloadTemplate={handleDownloadTemplate}
      />
    </div>
  );
};

export default MortgageContent;